import { Component } from "@theme/component";
import {
    ThemeEvents,
    CartAddEvent,
    CartErrorEvent,
    VariantUpdateEvent,
    VariantSelectedEvent,
    RugPadToggleEvent,
} from "@theme/events";
import { morph } from "@theme/morph";
import { shopifyFormatMoney } from "@theme/utilities";

export class AddToCartComponent extends Component {
    connectedCallback() {
        super.connectedCallback();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }
}

if (!customElements.get("add-to-cart-component")) {
    customElements.define("add-to-cart-component", AddToCartComponent);
}

if (!customElements.get("product-form-component")) {
    class ProductFormComponent extends Component {
        #abortController = new AbortController();
        connectedCallback() {
            //if (this.hasChildNodes()) this.init();
            super.connectedCallback();
            const { signal } = this.#abortController;

            const target = this.closest(".product-info, quick-add-drawer");

            target?.addEventListener(
                ThemeEvents.variantUpdate,
                this.#onVariantUpdate,
                { signal },
            );

            target?.addEventListener(
                ThemeEvents.variantSelected,
                this.#onVariantSelect,
                { signal },
            );

            target?.addEventListener(
                ThemeEvents.rugPadToggle,
                this.#onRugPadToggle,
                { signal },
            );
        }

        disconnectedCallback() {
            super.disconnectedCallback();

            this.#abortController.abort();
        }

        /**
         * Handles submission of the product form.
         * @param {object} evt - Event object.
         */
        async handleSubmit(evt) {
            if (evt.target.id === "product-signup_form") return;
            this.cartDrawer = document.querySelector("cart-drawer");
            window.dataLayer = window.dataLayer || [];

            evt.preventDefault();

            if (this.addToCartButton.getAttribute("aria-disabled") === "true")
                return;

            if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
                window.navigator.vibrate(100);
            }

            this.errorMsg = null;
            this.setErrorMsgState();

            // Disable "Add to Cart" button until submission is complete.
            this.addToCartButton.setAttribute("aria-disabled", "true");
            this.addToCartButton.classList.add("is-loading");

            const formData = new FormData(this.form);
            const data = {};
            const isCustomSize = !!this.closest('product-form-component')?.querySelector('custom-size-calculator');

            for (const [key, value] of formData.entries()) {
                if (isCustomSize) {
                    const match = key.match(/^(\w+)\[(.+)\]$/);
                    if (match) {
                        const [, parent, child] = match;
                        if (!data[parent]) data[parent] = {};
                        data[parent][child] = value;
                        continue;
                    }
                }
                data[key] = value;
            }
            const input = {
                items: [],
            };

            input.items.push(data);
            if (this.rugPadId) {
                input.items.push({
                    id: this.rugPadId,
                    quantity: 1,
                });
            }
            let sections = "cart-icon-bubble";
            if (this.cartDrawer) {
                sections += `,${this.cartDrawer.closest(".shopify-section").id.replace("shopify-section-", "")}`;
            }

            formData.append("sections_url", window.location.pathname);
            formData.append("sections", sections);
            input.sections = sections;

            const fetchRequestOpts = {
                method: "POST",
                headers: {
                    Accept: "application/javascript",
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify(input),
            };

            try {
                let openCustomRecoModal = false;
                const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
                if (!oldCartResponse.ok)
                    throw new Error(oldCartResponse.status);
                const oldCartData = await oldCartResponse.json();

                // when add to cart from empty cart, open custom recommendation modal, for now disabled
                /* if (oldCartData?.item_count <= 0) {
                    openCustomRecoModal = true;
                } */

                const response = await fetch(
                    theme.routes.cartAdd,
                    fetchRequestOpts,
                );
                const data = await response.json();
                let error =
                    typeof data.description === "string"
                        ? data.description
                        : data.message;
                if (data.errors && typeof data.errors === "object") {
                    error = Object.entries(data.errors).map((item) =>
                        item[1].join(", "),
                    );
                }

                if (data.status) this.setErrorMsgState(error);

                if (!response.ok) throw new Error(response.status);

                if (theme.settings.afterAtc === "page") {
                    // Allow the tick animation to complete
                    setTimeout(() => {
                        window.location.href = theme.routes.cart;
                    }, 300);
                } else {
                    // Update cart icon count.
                    ProductFormComponent.updateCartIcon(data);

                    // If item was added from Quick Add drawer, show "Added to cart" message.
                    const quickAddDrawer = this.closest("quick-add-drawer");
                    if (quickAddDrawer) quickAddDrawer.addedToCart();

                    setTimeout(() => {
                        // Update cart drawer contents.
                        if (this.cartDrawer) {
                            this.cartDrawer.renderContents(
                                data,
                                !quickAddDrawer &&
                                    !openCustomRecoModal &&
                                    theme.settings.afterAtc === "drawer",
                            );
                        } else if (
                            window.location.pathname === theme.routes.cart
                        ) {
                            const cartItems =
                                document.querySelector("cart-items");
                            if (cartItems) {
                                if (cartItems.dataset.empty === "true") {
                                    window.location.reload();
                                } else {
                                    cartItems.refresh();
                                }
                            }
                        }
                    }, 700);
                }

                const newCartResponse = await fetch(`${theme.routes.cart}.js`);
                if (!newCartResponse.ok)
                    throw new Error(newCartResponse.status);
                const newCartData = await newCartResponse.json();
                const itemInOldCart = oldCartData.items.filter(
                    (item) => item.variant_id === data.variant_id,
                );

                // Add to Cart Event for GTM eeAddtoCart
                if (data?.items) {
                    const products = data.items.map((cartItem) => {
                        return {
                            name: cartItem?.product_title,
                            id: cartItem?.sku,
                            price: shopifyFormatMoney(
                                cartItem?.price,
                                window.theme.settings.moneyFormat,
                                false,
                            )?.replace("$", ""),
                            category: cartItem?.product_type,
                            variant: cartItem?.variant_title,
                            quantity: 1,
                        };
                    });
                    const eeAddToCartData = {
                        event: "eeAddtoCart",
                        ecommerce: {
                            currencyCode: window.theme.settings.currency,
                            add: {
                                products: products,
                            },
                        },
                    };

                    window.dataLayer.push(eeAddToCartData);
                }
                // Check if product was already in the cart
                if (itemInOldCart.length > 0) {
                    const currentItem = data?.items?.find(
                        (e) => e.variant_id === item.variant_id,
                    );
                    itemInOldCart.forEach((item) => {
                        this.dispatchEvent(
                            new CustomEvent("on:line-item:change", {
                                bubbles: true,
                                detail: {
                                    cart: newCartData,
                                    variantId: item.variant_id,
                                    oldQuantity: item.quantity,
                                    newQuantity:
                                        item.quantity === currentItem.quantity
                                            ? itemInOldCart.quantity
                                            : currentItem.quantity,
                                },
                            }),
                        );
                    });
                } else {
                    this.dispatchEvent(
                        new CustomEvent("on:cart:add", {
                            bubbles: true,
                            detail: {
                                cart: newCartData,
                                variantId: data.variant_id,
                            },
                        }),
                    );
                }
                try {
                    if (openCustomRecoModal) {
                        const customReco = document.querySelector(
                            "#custom-cart-reco-modal",
                        );
                        if (customReco) {
                            customReco.setAttribute("open", "");
                        }
                    }
                } catch (error) {
                    console.log(error);
                }
            } catch (error) {
                console.log(error); // eslint-disable-line
                this.dispatchEvent(
                    new CustomEvent("on:cart:error", {
                        bubbles: true,
                        detail: {
                            error: this.errorMsg?.textContent,
                        },
                    }),
                );

                if (this.cartDrawer) this.cartDrawer.refresh(true);
            } finally {
                // Re-enable 'Add to Cart' button.
                this.addToCartButton.classList.add("is-success");
                this.addToCartButton.removeAttribute("aria-disabled");
                setTimeout(() => {
                    this.addToCartButton.classList.remove("is-loading");
                    this.addToCartButton.classList.remove("is-success");
                }, 1400);
            }
        }

        /**
         * @param {VariantSelectedEvent} event
         */
        #onVariantSelect = (event) => {
            if (!event?.detail?.resource?.id) return;
            this.addToCartButton.setAttribute("aria-disabled", "true");
            this.addToCartButton.classList.add("is-loading");
        };

        /**
         * @param {VariantUpdateEvent} event
         */
        #onVariantUpdate = (event) => {
            if (event.detail.data.newProduct) {
                this.dataset.productId = event.detail.data.newProduct.id;
            } else if (event.detail.data.productId !== this.dataset.productId) {
                return;
            }

            const { variantId, addToCartButtonContainer } = this.refs;

            const currentAddToCartButton =
                addToCartButtonContainer?.refs.addToCartButton;
            /* const newAddToCartButton = event.detail.data.html.querySelector(
                '[ref="addToCartButton"]',
            ); */

            if (!currentAddToCartButton) return;

            const variant = event?.detail?.data?.variant || null;

            // Update the button state
            if (variant == null || variant.available == false) {
                currentAddToCartButton.setAttribute("disabled", "true");
                currentAddToCartButton.innerHTML = "Sold Out";
                this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
                    "hidden",
                    "true",
                );
            } else {
                addToCartButtonContainer.refs.addToCartButton.removeAttribute(
                    "disabled",
                );
                this.refs.acceleratedCheckoutButtonContainer?.removeAttribute(
                    "hidden",
                );
                currentAddToCartButton.innerHTML = "Add To Cart";
            }

            // Update the add to cart button text and icon
            /*  if (newAddToCartButton) {
                morph(currentAddToCartButton, newAddToCartButton);
            } */

            if (!variant?.id) return;

            // Update the variant ID
            variantId.value = variant.id ?? "";

            // Set the data attribute for the add to cart button to the product variant media if it exists
            if (variant) {
                const productVariantMedia =
                    variant.featured_media?.preview_image?.src;
                productVariantMedia &&
                    addToCartButtonContainer?.setAttribute(
                        "data-product-variant-media",
                        productVariantMedia + "&width=100",
                    );
            }
            this.addToCartButton.removeAttribute("aria-disabled");
            this.addToCartButton.classList.remove("is-loading");
        };

        /**
         * @param {RugPadToggleEvent} event
         */
        #onRugPadToggle(event) {
            const form = this.querySelector(".js-product-form");
            if (event?.detail?.resource) {
                const newInput =
                    event.detail.resource.querySelector('input[name="id"]');
                form.setAttribute("rugpadId", newInput.value);
            } else {
                form.removeAttribute("rugpadId");
            }
        }

        checkRugPadAva;

        /**
         * Updates the cart icon count in the header.
         * @param {object} response - Response JSON.
         */
        static updateCartIcon(response) {
            const cartIconBubble = document.getElementById("cart-icon-bubble");
            if (cartIconBubble) {
                cartIconBubble.innerHTML =
                    response.sections["cart-icon-bubble"];
            }
        }

        /**
         * Shows/hides an error message.
         * @param {string} [error=false] - Error to show a message for.
         */
        setErrorMsgState(error = false) {
            this.errorMsg =
                this.errorMsg || this.querySelector(".js-form-error");
            if (!this.errorMsg) return;

            this.errorMsg.hidden = !error;
            if (error) {
                this.errorMsg.innerHTML = "";
                const errorArray = Array.isArray(error) ? error : [error];
                errorArray.forEach((err, index) => {
                    if (index > 0)
                        this.errorMsg.insertAdjacentHTML("beforeend", "<br>");
                    this.errorMsg.insertAdjacentText("beforeend", err);
                });
            }
        }

        get rugPadForm() {
            return null;
        }

        get rugPadId() {
            return this.querySelector(".js-product-form").getAttribute(
                "rugPadId",
            );
        }

        get addToCartButton() {
            return this.querySelector('[name="add"]');
        }

        get form() {
            return this.querySelector(".js-product-form");
        }
    }

    customElements.define("product-form-component", ProductFormComponent);
}
